/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { ChangeEvent } from 'react'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Select } from '@nexigendigital/nxui'
import { updateBillingCycle } from 'store/slices/shopSlice'
import { aud } from 'helpers/utils'
import { BaseHosting, PendingItem, Pricing } from 'models/shop'
import { useAppDispatch, useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface ConfigOption {
    id: number,
    name: string,
    description: string,
    input_type: string,
    values?: {
        display_order: number,
        id: number,
        name: string,
        pricing: Pricing[],
        show_order: number
    }[]
}

interface Props {
    productID: number,
    pricing: Pricing[],
    configOptions: ConfigOption[],
    currentProduct: BaseHosting | null,
    setCurrentProduct: (product: BaseHosting) => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function BillingCyclePricing({ productID, pricing, configOptions, currentProduct, setCurrentProduct }: Props) {
    const dispatch = useAppDispatch()
    const { items, baseConfigOptions } = useAppSelector((state) => state.shop)
    const isItemAddedToCart = items.some((addedItem: PendingItem) => addedItem.product_id === productID)
    const item: any = items.find((addedItem: PendingItem) => addedItem.product_id === productID)
    const baseConfigs = baseConfigOptions.map(({ name }) => name)

    function pricingBillingCycle(billing_cycle_id: number) {
        if (isItemAddedToCart && item.config_options) {
            const itemConfigs = item.config_options.map(({ config_id }: { config_id: number, option_id: number }) => config_id)

            return configOptions.map((config: ConfigOption) => {
                if (itemConfigs.includes(config.id)) {
                    const { option_id } = item.config_options.find(({ config_id }: { config_id: number, option_id: number }) => config_id === config.id)
                    const { values } = config
                    const value = values?.find(({ id }) => id === option_id)
                    const pricing = value?.pricing.find(({ billing_cycle: { id } }) => id === billing_cycle_id)

                    if (typeof pricing?.price === 'string') {
                        return parseFloat(pricing?.price)
                    }

                    return pricing?.price
                }

                return 0
            }).reduce((prev = 0, next = 0) => {
                return prev + next
            }, 0)
        }

        return configOptions.map((config) => {
            if (baseConfigs.includes(config.name)) {
                const baseConfigOption = baseConfigOptions.find(({ name }) => name === config.name)
                const { values } = config
                const value = values?.find(({ name }) => name === baseConfigOption?.value)
                const pricing = value?.pricing.find(({ billing_cycle: { id } }) => id === billing_cycle_id)

                if (typeof pricing?.price === 'string') {
                    return parseFloat(pricing?.price)
                }

                return pricing?.price
            }

            return 0
        }).reduce((prev = 0, next = 0) => {
            return prev + next
        }, 0)
    }

    return (
        <SelectWrapper>
            <Select
                value={currentProduct?.billing_cycle_id}
                disabled={!isItemAddedToCart}
                onChange={(e: ChangeEvent<{ value: string }>) => {
                    dispatch(updateBillingCycle({
                        product_id: productID,
                        billing_cycle_id: +e.target.value,
                        type: 'custom-hosting'
                    }))

                    if (currentProduct) {
                        setCurrentProduct({
                            ...currentProduct,
                            billing_cycle_id: +e.target.value
                        })
                    }
                }}
            >
                {pricing.map(({ billing_cycle: { id: billing_cycle_id, name } }) => {
                    return (
                        <option key={billing_cycle_id} value={billing_cycle_id}>
                            {aud(pricingBillingCycle(billing_cycle_id))}
                            {' /'}
                            {name}
                        </option>
                    )
                })}
            </Select>
        </SelectWrapper>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const SelectWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`
